import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { teilnehmerliste, type Teilnehmerzeile } from '../api/buchungen';
import {
  staende,
  stellvertretend,
  vorlage as ladeVorlage,
  type Checkinfeld,
  type Checkinstand,
  type Checkintext,
} from '../api/checkin';
import {
  akte as ladeAkte,
  platzBenennen,
  teilnehmerErfassen,
  type Personangabe,
  type Terminakte as Akte,
} from '../api/termine';
import { AbTablet } from '../komponenten/AbTablet';
import { Kennzahlenleiste } from '../komponenten/Akte';
import { Dialog } from '../komponenten/Dialog';
import { Feld } from '../komponenten/Feld';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import { Meldung } from '../komponenten/Meldung';
import type { Kopf } from '../komponenten/Rahmen';
import { Stellvertretung } from '../planung/Empfang';
import { Absagehinweis, stand, uhrzeit, zeitpunkt } from './Terminfenster';
import { KundeVerweis } from '../komponenten/Kontaktkarte';

/** Der Zustand in Worten — der Schlüssel ohne Umlaut ist kein Wort. */
function zustand(status: string): string {
  return status === 'durchgefuehrt' ? 'durchgeführt' : status;
}

function zustandsmarke(status: string): string {
  if (status === 'abgesagt') return 'marke marke--krit';
  if (status === 'buchbar') return 'marke marke--gut';
  return 'marke marke--inaktiv';
}

type Offen =
  | { readonly art: 'benennen'; readonly platz: Teilnehmerzeile }
  | { readonly art: 'erfassen' }
  | {
      readonly art: 'einchecken';
      readonly platz: Teilnehmerzeile;
      readonly texte: readonly Checkintext[];
      readonly felder: readonly Checkinfeld[];
    };

/**
 * DIE TERMINAKTE (CRM-117, Lücke L6 aus dem Durchgang CRM-53).
 *
 * Ein Termin mit allem, was an ihm hängt: Kopf mit Produkt, Zeit, Ort,
 * Trainer, Ressourcen und Zustand (BUC-66), darunter die Teilnehmenden mit dem
 * Weg zu Buchung, Auftrag und Kunde. Drei Handgriffe am Platz:
 *
 * * einen gebuchten Platz ohne Namen BENENNEN (BUC-70) — nur ohne Namen; der
 *   Tausch eines Namens ist der Teilnehmerwechsel nach BUC-142 in der Buchung,
 * * am Exklusivtermin weitere Teilnehmende ERFASSEN (BUC-68, BUC-69); über der
 *   vereinbarten Kapazität wird das ausgewiesen, nicht abgewiesen (BUC-82),
 * * stellvertretend EINCHECKEN wie am Empfang (TAG-62) — auch am
 *   Exklusivtermin.
 *
 * Telefon (ÄA-63): Benennen und Check-in sind Handgriffe und bleiben; das
 * Erfassen am Exklusivtermin ist Vertriebsarbeit und steht ab Tablet.
 */
export function Terminakte({
  id,
  darfTeilnehmerSehen,
  darfBenennen,
  darfCheckin,
  darfCheckinStand,
  beiKopf,
  beiBuchung,
  beiAuftrag,
  beiKunde,
  beiAngebot,
  beiTagesplan,
  beiAbsageliste,
}: {
  readonly id: string;
  /** `buchung/sehen` — die Teilnehmerliste mit Namen. */
  readonly darfTeilnehmerSehen: boolean;
  /** `buchung/benennen` (0312) — benennen und am Exklusivtermin erfassen. */
  readonly darfBenennen: boolean;
  /** `tagesplan/checkin` — stellvertretend einchecken (TAG-62). */
  readonly darfCheckin: boolean;
  /** `tagesplan/arbeitsliste_sehen` — wer schon eingecheckt ist. */
  readonly darfCheckinStand: boolean;
  /** Meldet den Aktenkopf an den Seitenkopf des Rahmens (ÄA-41). */
  readonly beiKopf?: (kopf: Kopf) => void;
  readonly beiBuchung?: (nummer: string) => void;
  readonly beiAuftrag?: (auftrag: string) => void;
  readonly beiKunde?: (kunde: string) => void;
  readonly beiAngebot?: (angebot: string) => void;
  readonly beiTagesplan?: (tag: string, standort: string | undefined) => void;
  /** BUC-131 (ÄW-DP-02): der Weg in die Absageliste, wenn der Termin abgesagt ist. */
  readonly beiAbsageliste?: () => void;
}): JSX.Element {
  const [akte, setzeAkte] = useState<Akte | null>(null);
  const [plaetze, setzePlaetze] = useState<readonly Teilnehmerzeile[]>([]);
  const [checkins, setzeCheckins] = useState<readonly Checkinstand[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [offen, setzeOffen] = useState<Offen | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    const [a, t, c] = await Promise.all([
      ladeAkte(id),
      darfTeilnehmerSehen ? teilnehmerliste(id) : Promise.resolve([]),
      darfCheckinStand ? staende([id]) : Promise.resolve([]),
    ]);
    setzeAkte(a);
    setzePlaetze(t);
    setzeCheckins(c);
  }, [id, darfTeilnehmerSehen, darfCheckinStand]);

  useEffect(() => {
    setzeAkte(null);
    void laden().catch((ursache: unknown) => {
      if (!(ursache instanceof ApiFehler)) throw ursache;
      setzeFehler(ursache);
    });
  }, [laden]);

  const aktiv = plaetze.filter((p) => p.buchungsstatus !== 'storniert');
  const benannt = aktiv.filter((p) => p.name !== null).length;
  const eingecheckt = new Map(checkins.map((c) => [c.teilnehmer, c]));

  useEffect(() => {
    if (beiKopf === undefined || akte === null) return;
    beiKopf({
      kicker: `Termin · ${akte.terminart === 'exklusiv' ? 'Exklusivtermin' : 'offener Termin'}`,
      titel: akte.produkt,
      unterzeile: `${zeitpunkt(akte.beginn)}–${uhrzeit(akte.ende)} Uhr${akte.ort === null ? '' : ` · ${akte.ort}`}`,
      sinnbild: 'calendar-blank',
      kennzeichen: <span className={zustandsmarke(akte.status)}>{zustand(akte.status)}</span>,
      kennzahlen: (
        <Kennzahlenleiste
          zahlen={[
            { beschriftung: 'Gebuchte Plätze', wert: String(akte.gebuchtePlaetze) },
            ...(darfTeilnehmerSehen ? [{ beschriftung: 'Mit Namen', wert: String(benannt) }] : []),
            { beschriftung: 'Anwesend erfasst', wert: String(akte.anwesenheitErfasst) },
            ...(akte.vereinbarung === null
              ? []
              : [{ beschriftung: 'Vereinbart', wert: String(akte.vereinbarung.vereinbarteKapazitaet) }]),
          ]}
        />
      ),
    });
  }, [akte, beiKopf, benannt, darfTeilnehmerSehen]);

  async function eincheckenOeffnen(platz: Teilnehmerzeile): Promise<void> {
    setzeMeldung(null);
    try {
      const v = await ladeVorlage(platz.id);
      setzeOffen({ art: 'einchecken', platz, texte: v.texte, felder: v.felder });
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    }
  }

  /** Nach einem Handgriff: Fenster zu, Meldung, neu laden. */
  async function danach(text: string): Promise<void> {
    setzeOffen(null);
    setzeFehler(null);
    setzeMeldung(text);
    await laden().catch((ursache: unknown) => {
      if (!(ursache instanceof ApiFehler)) throw ursache;
      setzeFehler(ursache);
    });
  }

  if (akte === null) {
    return (
      <div className="karte">
        {fehler === null ? <p className="hilfe">Wird geladen …</p> : <Meldung fehler={fehler} />}
      </div>
    );
  }

  const abgesagt = akte.status === 'abgesagt';
  const trainer = akte.ressourcen.filter((r) => r.art === 'trainer');
  const uebrige = akte.ressourcen.filter((r) => r.art !== 'trainer');
  const tag = akte.beginn.slice(0, 10);
  // BUC-68: Erfassen nur am Exklusivtermin mit Vereinbarung — der Kunde der Vereinbarung bucht.
  const erfassbar = darfBenennen && !abgesagt && akte.terminart === 'exklusiv' && akte.vereinbarung !== null;

  return (
    <>
      {fehler !== null && offen === null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <div className="karte">
        <h2>Termin</h2>
        {/* BUC-129, BUC-131: Grund und Fälle der Absage, mit dem Weg in die Absageliste. */}
        {akte.absage !== null && (
          <Absagehinweis
            absage={akte.absage}
            {...(beiAbsageliste === undefined ? {} : { beiAbsageliste })}
          />
        )}
        <dl className="angaben">
          <dt>Produkt</dt>
          <dd>
            {akte.produkt} <span className="hilfe">{akte.artikelnummer}</span>
          </dd>
          <dt>Zeit</dt>
          <dd>
            {beiTagesplan === undefined ? (
              <>
                {zeitpunkt(akte.beginn)} bis {uhrzeit(akte.ende)} Uhr
              </>
            ) : (
              <Maskenverweis
                maske={{ art: 'trainingsplanung', tag, ...(akte.ort === null ? {} : { standort: akte.ort }) }}
                title="In der Trainingsplanung öffnen"
                beiKlick={() => {
                  beiTagesplan(tag, akte.ort ?? undefined);
                }}
              >
                {zeitpunkt(akte.beginn)} bis {uhrzeit(akte.ende)} Uhr
              </Maskenverweis>
            )}
          </dd>
          <dt>Ort</dt>
          <dd>{akte.ort ?? 'kein Standort hinterlegt'}</dd>
          <dt>Trainer</dt>
          <dd>
            {trainer.length === 0
              ? 'noch keiner eingeteilt'
              : trainer.map((r) => `${r.bezeichnung} (${stand(r.status)})`).join(', ')}
          </dd>
          <dt>Ressourcen</dt>
          <dd>
            {uebrige.length === 0
              ? 'keine belegt'
              : uebrige.map((r) => `${r.bezeichnung} (${stand(r.status)})`).join(', ')}
          </dd>
          <dt>Zustand</dt>
          <dd>
            {akte.terminart === 'exklusiv' ? 'Exklusivtermin' : 'offener Termin'}{' '}
            <span className={zustandsmarke(akte.status)}>{zustand(akte.status)}</span>
          </dd>
          <dt>Plätze</dt>
          <dd>
            {akte.terminart === 'exklusiv'
              ? `${String(akte.hoechstteilnehmer)} (dem Kunden ganz zugeordnet)`
              : `${String(akte.belegt)} von ${String(akte.hoechstteilnehmer)} vergeben`}
            {akte.vereinbarung !== null
              && `, vereinbart ${String(akte.vereinbarung.vereinbarteKapazitaet)}`}
          </dd>
          {akte.kundenname !== null && (
            <>
              <dt>Kunde</dt>
              <dd>
                {akte.kunde === null || beiKunde === undefined ? (
                  akte.kundenname
                ) : (
                  /* Auftrag CRM-181: Kontaktkarte am Namen — beiKunde steht nur mit kunde/sehen. */
                  <KundeVerweis kundeId={akte.kunde} zurAkte={beiKunde}>
                    <Maskenverweis
                      maske={{ art: 'kundenakte', id: akte.kunde }}
                      beiKlick={() => {
                        beiKunde(akte.kunde!);
                      }}
                    >
                      {akte.kundenname}
                    </Maskenverweis>
                  </KundeVerweis>
                )}
              </dd>
            </>
          )}
          {(akte.angebotsnummer !== null || akte.auftragsnummer !== null) && (
            <>
              <dt>Herkunft</dt>
              <dd>
                {akte.angebotsnummer !== null
                  && (akte.herkunftAngebot === null || beiAngebot === undefined ? (
                    <>Angebot {akte.angebotsnummer} </>
                  ) : (
                    <>
                      <Maskenverweis
                        maske={{ art: 'angebotsakte', id: akte.herkunftAngebot }}
                        beiKlick={() => {
                          beiAngebot(akte.herkunftAngebot!);
                        }}
                      >
                        Angebot {akte.angebotsnummer}
                      </Maskenverweis>{' '}
                    </>
                  ))}
                {akte.auftragsnummer !== null
                  && (akte.herkunftAuftrag === null || beiAuftrag === undefined ? (
                    <>Auftrag {akte.auftragsnummer}</>
                  ) : (
                    <Maskenverweis
                      maske={{ art: 'auftragsakte', id: akte.herkunftAuftrag }}
                      beiKlick={() => {
                        beiAuftrag(akte.herkunftAuftrag!);
                      }}
                    >
                      Auftrag {akte.auftragsnummer}
                    </Maskenverweis>
                  ))}
              </dd>
            </>
          )}
        </dl>
      </div>

      {darfTeilnehmerSehen && (
        <div className="karte">
          <h2>Teilnehmende</h2>
          {akte.vereinbarung !== null && benannt > akte.vereinbarung.vereinbarteKapazitaet && (
            /* BUC-82: ausgewiesen, nicht abgewiesen — die Folge regelt PRD-47. */
            <p className="streifen streifen--warn" role="note">
              {benannt} Teilnehmende gemeldet, vereinbart sind{' '}
              {akte.vereinbarung.vereinbarteKapazitaet}. Die Überschreitung wird nach der
              Vereinbarung behandelt.
            </p>
          )}
          {erfassbar && (
            <AbTablet teil>
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf"
                  onClick={() => {
                    setzeMeldung(null);
                    setzeFehler(null);
                    setzeOffen({ art: 'erfassen' });
                  }}
                >
                  Teilnehmende erfassen
                </button>
              </div>
            </AbTablet>
          )}
          {aktiv.length === 0 ? (
            <p className="hilfe">Noch keine Plätze gebucht.</p>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption className="nur-vorlesen">Teilnehmende am Termin</caption>
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Buchung</th>
                    <th scope="col">Auftrag</th>
                    <th scope="col">Kunde</th>
                    <th scope="col">Check-in</th>
                  </tr>
                </thead>
                <tbody>
                  {aktiv.map((p) => {
                    const ci = eingecheckt.get(p.id);
                    return (
                      <tr key={p.id}>
                        <td>
                          {p.name ?? (
                            <>
                              <span className="marke marke--warn">ohne Namen</span>{' '}
                              {darfBenennen && !abgesagt && (
                                <button
                                  type="button"
                                  className="knopf knopf--verweis"
                                  data-auch-telefon
                                  onClick={() => {
                                    setzeMeldung(null);
                                    setzeFehler(null);
                                    setzeOffen({ art: 'benennen', platz: p });
                                  }}
                                >
                                  Benennen
                                </button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="liste__nummer">
                          {beiBuchung === undefined ? (
                            p.buchungsnummer
                          ) : (
                            <Maskenverweis
                              maske={{ art: 'buchungsvorgang', nummer: p.buchungsnummer }}
                              beiKlick={() => {
                                beiBuchung(p.buchungsnummer);
                              }}
                            >
                              {p.buchungsnummer}
                            </Maskenverweis>
                          )}
                        </td>
                        <td className="liste__nummer">
                          {p.auftrag === null || p.auftragsnummer === null ? (
                            '—'
                          ) : beiAuftrag === undefined ? (
                            p.auftragsnummer
                          ) : (
                            <Maskenverweis
                              maske={{ art: 'auftragsakte', id: p.auftrag }}
                              beiKlick={() => {
                                beiAuftrag(p.auftrag!);
                              }}
                            >
                              {p.auftragsnummer}
                            </Maskenverweis>
                          )}
                        </td>
                        <td>
                          {beiKunde === undefined ? (
                            p.kundenname
                          ) : (
                            <KundeVerweis kundeId={p.kunde} zurAkte={beiKunde}>
                              <Maskenverweis
                                maske={{ art: 'kundenakte', id: p.kunde }}
                                beiKlick={() => {
                                  beiKunde(p.kunde);
                                }}
                              >
                                {p.kundenname}
                              </Maskenverweis>
                            </KundeVerweis>
                          )}
                        </td>
                        <td>
                          {ci !== undefined ? (
                            <span className="marke marke--gut">
                              {uhrzeit(ci.abgeschlossenAm)} Uhr{ci.art === 'fremd' ? ', am Tresen' : ''}
                            </span>
                          ) : darfCheckin && !abgesagt && p.name !== null ? (
                            /* TAG-62: auch am Exklusivtermin — kein Weg filtert ihn aus. */
                            <button
                              type="button"
                              className="knopf knopf--verweis"
                              data-auch-telefon
                              onClick={() => void eincheckenOeffnen(p)}
                            >
                              Einchecken
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {offen?.art === 'benennen' && (
        <Benennen
          termin={akte.id}
          platz={offen.platz}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
          beiErfolg={(name) => danach(`Der Platz in Buchung ${offen.platz.buchungsnummer} trägt jetzt den Namen ${name}.`)}
        />
      )}

      {offen?.art === 'erfassen' && akte.vereinbarung !== null && (
        <Erfassen
          termin={akte.id}
          gemeldet={benannt}
          vereinbart={akte.vereinbarung.vereinbarteKapazitaet}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
          beiErfolg={(e) =>
            danach(
              `${String(e.erfasst)} Teilnehmende in Buchung ${e.buchungsnummer} erfasst.`
                + (e.ueberschritten
                  ? ` Gemeldet sind jetzt ${String(e.gemeldet)}, vereinbart ${String(e.vereinbarteKapazitaet)} — die Überschreitung ist ausgewiesen.`
                  : ''),
            )}
        />
      )}

      {offen?.art === 'einchecken' && (
        <Dialog
          titel="Einchecken"
          unterzeile={offen.platz.name ?? undefined}
          beiSchliessen={() => {
            setzeOffen(null);
          }}
        >
          <Stellvertretung
            name={offen.platz.name ?? ''}
            texte={offen.texte}
            felder={offen.felder}
            beiAbbruch={() => {
              setzeOffen(null);
            }}
            beiAbschluss={async (abschluss) => {
              await stellvertretend(offen.platz.id, abschluss);
              await danach(`${offen.platz.name ?? ''} ist eingecheckt — als Fremderfassung vermerkt.`);
            }}
          />
        </Dialog>
      )}
    </>
  );
}

/** Die Angaben zu einer Person, ohne leere Felder. */
function angabe(name: string, email: string, geburtsdatum: string): Personangabe {
  return {
    name: name.trim(),
    ...(email.trim() === '' ? {} : { email: email.trim() }),
    ...(geburtsdatum === '' ? {} : { geburtsdatum }),
  };
}

/**
 * BUC-70: einen Platz benennen. Das Pop-up ist die Rückfrage: Ein Name steht
 * danach fest und wechselt nur noch über den Teilnehmerwechsel (BUC-142).
 */
function Benennen({
  termin,
  platz,
  beiSchliessen,
  beiErfolg,
}: {
  readonly termin: string;
  readonly platz: Teilnehmerzeile;
  readonly beiSchliessen: () => void;
  readonly beiErfolg: (name: string) => Promise<void>;
}): JSX.Element {
  const [name, setzeName] = useState('');
  const [email, setzeEmail] = useState('');
  const [geburtsdatum, setzeGeburtsdatum] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  return (
    <Dialog
      titel="Platz benennen?"
      unterzeile={`Buchung ${platz.buchungsnummer} · ${platz.kundenname}`}
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== null && <Meldung fehler={fehler} />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setzeLaeuft(true);
          void platzBenennen(termin, platz.id, angabe(name, email, geburtsdatum))
            .then((r) => beiErfolg(r.name))
            .catch((ursache: unknown) => {
              if (!(ursache instanceof ApiFehler)) throw ursache;
              setzeFehler(ursache);
            })
            .finally(() => {
              setzeLaeuft(false);
            });
        }}
      >
        <Feld beschriftung="Name" wert={name} aendern={setzeName} pflicht />
        <Feld beschriftung="E-Mail" art="email" wert={email} aendern={setzeEmail} />
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="ta-geburtsdatum">
            Geburtsdatum
          </label>
          <input
            id="ta-geburtsdatum"
            type="date"
            className="feld__eingabe"
            value={geburtsdatum}
            onChange={(e) => {
              setzeGeburtsdatum(e.target.value);
            }}
          />
        </div>
        <p className="hilfe">
          Der Name steht danach am Platz. Ein anderer Name ist ein Teilnehmerwechsel in der Buchung.
        </p>
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf" data-auch-telefon onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            className="knopf knopf--haupt"
            data-auch-telefon
            disabled={laeuft || name.trim() === ''}
          >
            Benennen
          </button>
        </div>
      </form>
    </Dialog>
  );
}

type Zeile = { readonly schluessel: number; readonly name: string; readonly email: string };

/** BUC-68, BUC-69, BUC-82: Teilnehmende am Exklusivtermin erfassen. */
function Erfassen({
  termin,
  gemeldet,
  vereinbart,
  beiSchliessen,
  beiErfolg,
}: {
  readonly termin: string;
  readonly gemeldet: number;
  readonly vereinbart: number;
  readonly beiSchliessen: () => void;
  readonly beiErfolg: (ergebnis: Awaited<ReturnType<typeof teilnehmerErfassen>>) => Promise<void>;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Zeile[]>([{ schluessel: 0, name: '', email: '' }]);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const gefuellt = zeilen.filter((z) => z.name.trim() !== '');
  const danach = gemeldet + gefuellt.length;

  const aendere = (schluessel: number, feld: 'name' | 'email', wert: string): void => {
    setzeZeilen(zeilen.map((z) => (z.schluessel === schluessel ? { ...z, [feld]: wert } : z)));
  };

  return (
    <Dialog
      titel="Teilnehmende erfassen?"
      unterzeile="Für den Kunden der Vereinbarung, ohne zweiten Preis neben der Pauschale"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== null && <Meldung fehler={fehler} />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setzeLaeuft(true);
          void teilnehmerErfassen(
            termin,
            gefuellt.map((z) => angabe(z.name, z.email, '')),
          )
            .then(beiErfolg)
            .catch((ursache: unknown) => {
              if (!(ursache instanceof ApiFehler)) throw ursache;
              setzeFehler(ursache);
            })
            .finally(() => {
              setzeLaeuft(false);
            });
        }}
      >
        {zeilen.map((z, i) => (
          <fieldset key={z.schluessel} className="unterblock">
            <legend>Person {i + 1}</legend>
            <Feld
              beschriftung={`Name, Person ${String(i + 1)}`}
              wert={z.name}
              aendern={(neu) => {
                aendere(z.schluessel, 'name', neu);
              }}
            />
            <Feld
              beschriftung={`E-Mail, Person ${String(i + 1)}`}
              art="email"
              wert={z.email}
              aendern={(neu) => {
                aendere(z.schluessel, 'email', neu);
              }}
            />
          </fieldset>
        ))}
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            disabled={zeilen.length >= 30}
            onClick={() => {
              setzeZeilen([...zeilen, { schluessel: Math.max(...zeilen.map((z) => z.schluessel)) + 1, name: '', email: '' }]);
            }}
          >
            Weitere Person
          </button>
        </div>
        {/* BUC-82: bei jeder Meldung geprüft und ausgewiesen, nicht abgewiesen. */}
        <p className={danach > vereinbart ? 'streifen streifen--warn' : 'hilfe'} role="note">
          Danach {danach} Teilnehmende gemeldet, vereinbart sind {vereinbart}.
          {danach > vereinbart && ' Die Überschreitung wird ausgewiesen und nach der Vereinbarung behandelt.'}
        </p>
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft || gefuellt.length === 0}>
            Erfassen
          </button>
        </div>
      </form>
    </Dialog>
  );
}
